import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Lock, PackageCheck, RefreshCw, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tatva-admin-token";

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function normalizeReference(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, "");
}

export default function AdminOrders() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => window.localStorage.getItem(TOKEN_KEY) || "");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const duplicateReferenceMap = useMemo(() => {
    const map = new Map();
    orders.forEach((order) => {
      const key = normalizeReference(order.payment_reference);
      if (!key) return;
      map.set(key, [...(map.get(key) || []), order]);
    });
    return map;
  }, [orders]);
  const duplicateGroups = useMemo(
    () => Array.from(duplicateReferenceMap.values()).filter((group) => group.length > 1),
    [duplicateReferenceMap],
  );
  const duplicateOrderIds = useMemo(() => {
    const ids = new Set();
    duplicateGroups.forEach((group) => group.forEach((order) => ids.add(order.id)));
    return ids;
  }, [duplicateGroups]);

  const filterOptions = useMemo(() => [
    { label: "All", testId: "admin-filter-all", count: orders.length },
    { label: "New", testId: "admin-filter-new", count: orders.filter((order) => (order.order_status || "New order") === "New order").length },
    { label: "Payment Verified", testId: "admin-filter-payment-verified", count: orders.filter((order) => order.payment_status === "Payment verified").length },
    { label: "Packed", testId: "admin-filter-packed", count: orders.filter((order) => order.order_status === "Packed").length },
    { label: "Delivered", testId: "admin-filter-delivered", count: orders.filter((order) => order.order_status === "Delivered").length },
    { label: "Duplicate", testId: "admin-filter-duplicate", count: duplicateOrderIds.size },
  ], [duplicateOrderIds.size, orders]);

  const filteredOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return orders.filter((order) => {
      const isDuplicate = duplicateOrderIds.has(order.id);
      const matchesFilter =
        statusFilter === "All" ||
        (statusFilter === "New" && (order.order_status || "New order") === "New order") ||
        (statusFilter === "Payment Verified" && order.payment_status === "Payment verified") ||
        (statusFilter === "Packed" && order.order_status === "Packed") ||
        (statusFilter === "Delivered" && order.order_status === "Delivered") ||
        (statusFilter === "Duplicate" && isDuplicate);

      if (!matchesFilter) return false;
      if (!query) return true;

      const searchable = [
        order.id,
        order.payment_reference,
        order.customer?.full_name,
        order.customer?.phone,
        order.customer?.email,
      ].filter(Boolean).join(" ").toLowerCase();

      return searchable.includes(query);
    });
  }, [duplicateOrderIds, orders, searchQuery, statusFilter]);
  const visibleDuplicateCount = useMemo(
    () => filteredOrders.filter((order) => duplicateOrderIds.has(order.id)).length,
    [duplicateOrderIds, filteredOrders],
  );

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/admin/orders`, { headers });
      setOrders(response.data.orders || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load orders.");
      if (err?.response?.status === 401) {
        window.localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const login = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      window.localStorage.setItem(TOKEN_KEY, response.data.token);
      setToken(response.data.token);
      setPassword("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    window.localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setOrders([]);
  };

  const updateStatus = async (orderId, payload) => {
    setLoading(true);
    setError("");
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, payload, { headers });
      await fetchOrders();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update order.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-[#FAFAF8] pt-32" data-testid="admin-login-page">
        <section className="mx-auto max-w-md px-6 pb-24 md:px-10">
          <form data-testid="admin-login-form" onSubmit={login} className="rounded-[2rem] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <Lock className="mb-6 h-8 w-8 text-[#2A4B3C]" />
            <h1 data-testid="admin-login-heading" className="text-4xl font-light tracking-tight text-[#2C2C2A]">Admin Orders</h1>
            <p data-testid="admin-login-copy" className="mt-3 text-[#6B6B66]">Enter the admin password to view Tatva Hills orders.</p>
            <input
              data-testid="admin-password-input"
              required
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Admin password"
              className="mt-7 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-5 outline-none"
            />
            {error && <p data-testid="admin-login-error" className="mt-4 rounded-[1rem] bg-[#FFF4EF] p-3 text-sm text-[#C84B31]">{error}</p>}
            <button data-testid="admin-login-submit-button" type="submit" disabled={loading} className="mt-6 inline-flex w-full justify-center rounded-full bg-[#2A4B3C] px-8 py-4 font-medium text-white disabled:opacity-60">
              {loading ? "Checking…" : "Open Orders"}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAFAF8] pt-32" data-testid="admin-orders-page">
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p data-testid="admin-orders-eyebrow" className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C84B31]">Private dashboard</p>
            <h1 data-testid="admin-orders-heading" className="text-5xl font-light tracking-tight text-[#2C2C2A]">Orders</h1>
            <p data-testid="admin-orders-count" className="mt-3 text-[#6B6B66]">{filteredOrders.length} of {orders.length} orders shown</p>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-refresh-button" type="button" onClick={fetchOrders} className="inline-flex items-center gap-2 rounded-full border border-[#D5D5D0] bg-white px-5 py-3 text-sm font-medium text-[#2A4B3C]"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button data-testid="admin-logout-button" type="button" onClick={logout} className="inline-flex rounded-full bg-[#2A4B3C] px-5 py-3 text-sm font-medium text-white">Logout</button>
          </div>
        </div>

        {error && <p data-testid="admin-orders-error" className="mb-6 rounded-[1rem] bg-[#FFF4EF] p-4 text-sm text-[#C84B31]">{error}</p>}

        <div data-testid="admin-order-search-filter-panel" className="mb-6 rounded-[2rem] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <label className="block text-sm font-medium text-[#4A3B32]">Search orders
            <input
              data-testid="admin-order-search-input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by customer name, phone, order ID, or UPI reference"
              className="mt-2 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-5 outline-none"
            />
          </label>
          <div data-testid="admin-status-filter-buttons" className="mt-5 flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <button
                data-testid={option.testId}
                key={option.label}
                type="button"
                onClick={() => setStatusFilter(option.label)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${statusFilter === option.label ? "border-[#2A4B3C] bg-[#2A4B3C] text-white" : "border-[#D5D5D0] bg-[#FAFAF8] text-[#4A3B32] hover:border-[#2A4B3C]"}`}
              >
                {option.label} <span className="opacity-70">({option.count})</span>
              </button>
            ))}
          </div>
        </div>

        {visibleDuplicateCount > 0 && (
          <div data-testid="admin-duplicate-summary" className="mb-6 rounded-[1.5rem] border-2 border-[#C84B31] bg-[#FFF4EF] p-5 text-[#C84B31]">
            <div className="flex gap-3">
              <AlertTriangle className="mt-1 h-6 w-6 shrink-0" />
              <div>
                <p className="text-lg font-semibold">Possible duplicate orders found</p>
                <p data-testid="admin-duplicate-summary-copy" className="mt-1 text-sm leading-relaxed">
                  {visibleDuplicateCount} visible {visibleDuplicateCount === 1 ? "order uses" : "orders use"} a repeated UPI reference. Review before packing or marking delivered.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-5" data-testid="admin-orders-list">
          {orders.length === 0 && <div data-testid="admin-empty-orders" className="rounded-[2rem] bg-white p-8 text-[#6B6B66] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">No orders yet.</div>}
          {orders.length > 0 && filteredOrders.length === 0 && <div data-testid="admin-no-filter-results" className="rounded-[2rem] bg-white p-8 text-[#6B6B66] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">No orders match this search or filter.</div>}
          {filteredOrders.map((order) => {
            const referenceKey = normalizeReference(order.payment_reference);
            const duplicateOrders = duplicateReferenceMap.get(referenceKey) || [];
            const isDuplicate = duplicateOrders.length > 1;
            return (
            <article data-testid={`admin-order-card-${order.id}`} key={order.id} className={`rounded-[2rem] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${isDuplicate ? "border-2 border-[#C84B31]" : "border border-transparent"}`}>
              <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 data-testid={`admin-order-customer-${order.id}`} className="text-2xl font-normal text-[#2C2C2A]">{order.customer?.full_name || "Customer"}</h2>
                    <span data-testid={`admin-order-payment-status-${order.id}`} className="rounded-full bg-[#F0F0EA] px-3 py-1 text-xs font-medium text-[#4A3B32]">{order.payment_status}</span>
                    <span data-testid={`admin-order-status-${order.id}`} className="rounded-full bg-[#E8F0EA] px-3 py-1 text-xs font-medium text-[#2A4B3C]">{order.order_status || "New order"}</span>
                    {isDuplicate && <span data-testid={`admin-duplicate-badge-${order.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#C84B31] px-3 py-1 text-xs font-semibold text-white"><AlertTriangle className="h-3 w-3" /> Duplicate UPI Ref</span>}
                  </div>
                  {isDuplicate && (
                    <div data-testid={`admin-duplicate-detail-${order.id}`} className="mt-4 rounded-[1.25rem] bg-[#FFF4EF] p-4 text-sm leading-relaxed text-[#C84B31]">
                      <strong>Possible duplicate:</strong> this UPI reference appears in {duplicateOrders.length} orders — {duplicateOrders.map((duplicate) => duplicate.id.slice(0, 8)).join(", ")}.
                    </div>
                  )}
                  <p data-testid={`admin-order-id-${order.id}`} className="mt-2 text-sm text-[#6B6B66]">Order ID: {order.id}</p>
                  <p data-testid={`admin-order-date-${order.id}`} className="mt-1 text-sm text-[#6B6B66]">{formatDate(order.created_at)}</p>
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <div data-testid={`admin-order-contact-${order.id}`} className="rounded-[1.25rem] bg-[#FAFAF8] p-4 text-sm leading-relaxed text-[#4A3B32]">
                      <strong>Contact</strong><br />{order.customer?.phone}<br />{order.customer?.email}
                    </div>
                    <div data-testid={`admin-order-address-${order.id}`} className="rounded-[1.25rem] bg-[#FAFAF8] p-4 text-sm leading-relaxed text-[#4A3B32]">
                      <strong>Address</strong><br />{order.customer?.address}, {order.customer?.city} - {order.customer?.pincode}
                    </div>
                  </div>
                  <div className="mt-5 rounded-[1.25rem] bg-[#FAFAF8] p-4" data-testid={`admin-order-items-${order.id}`}>
                    <strong className="text-sm text-[#4A3B32]">Items</strong>
                    <div className="mt-3 space-y-2">
                      {order.items?.map((item) => (
                        <div key={`${order.id}-${item.product_id}-${item.pack_size}`} className="flex justify-between gap-4 text-sm text-[#6B6B66]">
                          <span>{item.name} × {item.quantity} ({item.pack_size})</span>
                          <strong className="text-[#2C2C2A]">₹{item.quantity * item.unit_price}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <aside className="rounded-[1.5rem] border border-[#EAEAE6] p-5" data-testid={`admin-order-actions-${order.id}`}>
                  <p className="text-sm text-[#6B6B66]">Total</p>
                  <p data-testid={`admin-order-subtotal-${order.id}`} className="text-3xl font-semibold text-[#2A4B3C]">₹{order.subtotal}</p>
                  <p data-testid={`admin-order-upi-reference-${order.id}`} className={`mt-4 rounded-[1rem] p-3 text-sm ${isDuplicate ? "bg-[#FFF4EF] text-[#C84B31]" : "bg-[#FAFAF8] text-[#4A3B32]"}`}>UPI Ref: <strong>{order.payment_reference}</strong></p>
                  <div className="mt-5 grid gap-3">
                    <button data-testid={`admin-mark-payment-verified-${order.id}`} type="button" onClick={() => updateStatus(order.id, { payment_status: "Payment verified" })} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2A4B3C] px-5 py-3 text-sm font-medium text-white"><CheckCircle2 className="h-4 w-4" /> Mark Payment Verified</button>
                    <button data-testid={`admin-mark-packed-${order.id}`} type="button" onClick={() => updateStatus(order.id, { order_status: "Packed" })} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D5D5D0] px-5 py-3 text-sm font-medium text-[#2A4B3C]"><PackageCheck className="h-4 w-4" /> Mark Packed</button>
                    <button data-testid={`admin-mark-delivered-${order.id}`} type="button" onClick={() => updateStatus(order.id, { order_status: "Delivered" })} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D5D5D0] px-5 py-3 text-sm font-medium text-[#2A4B3C]"><Truck className="h-4 w-4" /> Mark Delivered</button>
                  </div>
                </aside>
              </div>
            </article>
          );
          })}
        </div>
      </section>
    </main>
  );
}